<template>
  <div class="p-4 w-full">
    <div>
      <h1 class="text-xl md:text-2xl lg:text-3xl font-bold mb-4 text-white">
        {{ $t("audits.title") }}
      </h1>
    </div>
    <!-- Credit counters -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div class="bg-white p-3 md:p-4 rounded shadow">
        <div class="text-xl md:text-2xl lg:text-3xl font-bold primary-text">
          {{ subscription?.data?.remain_credit_phishing }}
        </div>
        <div class="text-sm md:text-base">{{ $t("audits.phishing_left") }}</div>
      </div>
      <div class="bg-white p-3 md:p-4 rounded shadow">
        <div class="text-xl md:text-2xl lg:text-3xl font-bold primary-text">
          {{ subscription?.data?.remain_credit_smshing }}
        </div>
        <div class="text-sm md:text-base">{{ $t("audits.smishing_left") }}</div>
      </div>
    </div>

    <div class="flex flex-col md:flex-row justify-between gap-4 mb-6">
      <!-- Custom Tab navigation -->
      <div class="flex flex-wrap gap-2">
        <Button
          asChild
          v-for="tab in tabs"
          :key="tab.label"
          custom
          class="w-full sm:w-auto"
        >
          <router-link
            :to="tab.route"
            :class="[
              'px-3 md:px-6 py-2 font-semibold rounded-full transition flex items-center gap-2 text-sm md:text-base w-full justify-center sm:w-auto',
              tab.route == activeTab
                ? 'bg-white primary-text shadow'
                : 'text-white bg-transparent hover:bg-white/20',
            ]"
            @click="navigate"
          >
            <span>{{ tab.label }}</span>
          </router-link>
        </Button>
      </div>
      <!-- New campaign button -->
      <div class="flex justify-center md:justify-end items-center">
        <Button
          :label="$t('audits.new_campaign')"
          icon="pi pi-plus"
          class="text-xs md:text-sm rounded-full spect-btn-primary w-full md:w-auto"
          @click="handleNewCampaign"
        />
      </div>
    </div>

    <div class="mt-16 md:mt-32 flex flex-col gap-4 md:gap-8">
      <!-- Search field -->
      <div class="w-full md:w-80">
        <IconField class="w-full">
          <InputIcon class="pi pi-search" />
          <InputText
            v-model="searchQuery"
            :placeholder="$t('audits.search')"
            class="w-full"
          />
        </IconField>
      </div>
      <div
        class="mt-2 border-2 px-2 md:px-3 py-2 md:py-4 radius-20 border-spect-gray overflow-x-auto"
      >
        <DataTable
          :value="campaignsSkeleton"
          v-if="isLoading"
          class="text-sm md:text-base"
        >
          <Column field="name" :header="$t('audits.table.name')">
            <template #body><Skeleton></Skeleton></template>
          </Column>
          <Column field="status" :header="$t('audits.table.status')">
            <template #body><Skeleton></Skeleton></template>
          </Column>
          <Column
            field="type"
            :header="$t('audits.table.type')"
            class="hidden md:table-cell"
          >
            <template #body><Skeleton></Skeleton></template>
          </Column>
          <Column
            field="created_at"
            :header="$t('audits.table.created_at')"
            class="hidden lg:table-cell"
          >
            <template #body><Skeleton></Skeleton></template>
          </Column>
          <Column :header="$t('audits.table.updated_at')" class="hidden xl:table-cell">
            <template #body><Skeleton></Skeleton></template>
          </Column>
          <Column :header="$t('audits.table.organisation')" class="hidden lg:table-cell">
            <template #body><Skeleton></Skeleton></template>
          </Column>
          <Column :header="$t('audits.table.actions')">
            <template #body><Skeleton></Skeleton></template>
          </Column>
        </DataTable>

        <DataTable
          :lazy="true"
          :value="filteredCampaigns"
          :stripedRows="true"
          :size="'normal'"
          :paginator="true"
          :rows="50"
          :totalRecords="campaigns?.totalRecords"
          @page="onPage"
          :rowsPerPageOptions="[20, 50, 100, 150, 200]"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="{first} à {last} sur {totalRecords}"
          responsiveLayout="stack"
          breakpoint="960px"
          v-model:selection="selectedCampaigns"
          dataKey="id"
          class="p-datatable-sm text-sm md:text-base"
          v-model:filters="filters"
          :globalFilterFields="globalFilterFields"
          v-else
        >
          <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
          <Column field="name" :header="$t('audits.table.name')" sortable>
            <template #body v-if="isTableLoading"
              ><Skeleton></Skeleton
            ></template>
          </Column>
          <Column field="status" :header="$t('audits.table.status')" sortable>
            <template #body v-if="isTableLoading"
              ><Skeleton></Skeleton
            ></template>

            <template #body="slotProps" v-else>
              <Tag
                :value="translateStatus(slotProps.data.status)"
                :severity="getStatusSeverity(slotProps.data.status)"
                class="no-whitespace"
              />
            </template>
          </Column>
          <Column
            field="type"
            :header="$t('audits.table.type')"
            sortable
            class="hidden md:table-cell"
          >
            <template #body v-if="isTableLoading"
              ><Skeleton></Skeleton
            ></template>

            <template #body="slotProps" v-else>
              {{ slotProps.data.type == "smshing" ? "Smishing" : "Phishing" }}
            </template>
          </Column>
          <Column
            field="created_at"
            :header="$t('audits.table.created_at')"
            sortable
            class="hidden lg:table-cell no-whitespace"
          >
            <template #body v-if="isTableLoading"
              ><Skeleton></Skeleton
            ></template>
            <template #body="slotProps" v-else>
              {{ formatDate(slotProps.data.created_at) }}
            </template>
          </Column>
          <Column
            field="launch_date"
            :header="$t('audits.table.updated_at')"
            sortable
            class="hidden xl:table-cell no-whitespace"
          >
            <template #body v-if="isTableLoading"
              ><Skeleton></Skeleton
            ></template>
            <template #body="slotProps" v-else>
              {{ formatDate(slotProps.data.launch_date) }}
            </template>
          </Column>
          <Column
            :header="$t('audits.table.organisation')"
            class="hidden lg:table-cell"
          >
            <template #body v-if="isTableLoading"
              ><Skeleton></Skeleton
            ></template>
            <template #body="slotProps" v-else>
              {{ slotProps.data.organisation?.name }}
            </template>
          </Column>
          <Column :header="$t('audits.table.actions')" style="width: 8rem">
            <template #body v-if="isTableLoading"
              ><Skeleton></Skeleton
            ></template>
            <template #body="slotProps" v-else>
              <div class="flex gap-2 justify-center md:justify-start">
                <Button
                  icon="pi pi-eye"
                  class="p-button-rounded p-button-text p-button-sm"
                  @click="goToDetails(slotProps.data.id)"
                />
                <Button
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-text p-button-danger p-button-sm"
                  @click="confirmDeleteCampaign(slotProps.data)"
                />
                <Button
                  icon="pi pi-download"
                  class="p-button-rounded p-button-text p-button-success p-button-sm"
                  @click="downloadReport(slotProps.data)"
                  v-if="activeTab.includes('audits#rapports')"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
    <!-- Delete Confirmation Dialog -->
    <Dialog
      v-model:visible="showDeleteModal"
      :header="$t('audits.delete_campaign')"
      :modal="true"
      class="p-fluid"
      :style="{ width: '450px' }"
    >
      <div class="confirmation-content">
        <div class="flex align-items-center justify-content-start">
          <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
          <span>{{ $t("audits.delete_confirmation") }}</span>
        </div>

        <div class="mt-3">
          <label for="campaignName" class="block text-900 font-medium mb-2">
            {{ $t("audits.type_campaign_name") }}
          </label>
          <InputText
            id="campaignName"
            v-model="deleteConfirmationName"
            :placeholder="campaignToDelete?.name"
            class="w-full"
          />
        </div>
      </div>

      <template #footer>
        <Button
          :label="$t('common.cancel')"
          icon="pi pi-times"
          class="p-button-text spect-btn-text"
          @click="cancelDelete"
        />
        <Button
          :label="$t('common.delete')"
          icon="pi pi-check"
          class="p-button-danger"
          @click="goDeleteCampaign"
          :disabled="deleteConfirmationName !== campaignToDelete?.name"
        />
      </template>
    </Dialog>
    <Toast position="bottom-right" />
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "primevue/usetoast";

import {
  fetchCampaigns,
  downloadCampaignReport,
  deleteCampaign,
} from "@/services/campaignService"; // <-- Add this import
import { fetchUserSubscription } from "@/services/subscriptionService";
//import Tabs from 'primevue/tabs';
//import TabList from 'primevue/tablist';
//import Tab from 'primevue/tab';

import Button from "primevue/button";
import InputText from "primevue/inputtext";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tag from "primevue/tag";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Skeleton from "primevue/skeleton";
import Toast from "primevue/toast";
import Dialog from "primevue/dialog";

const route = useRoute();
const router = useRouter();
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const toast = useToast();

const showDeleteModal = ref(false);
const deleteConfirmationName = ref("");
const campaignToDelete = ref();
const goToDetails = (id) => {
  //console.log(campaigns.value.campaigns)
  // Find the campaign by id
  const campaign = campaigns.value.campaigns.find((c) => c.id === id);

  // Check if it's a smishing (SMS) campaign or phishing (email) campaign
  if (campaign && campaign.type == "smshing") {
    //router.push(`/audits/smishing/details/${id}`);
    window.location.href = `/audits/smishing/details/${id}`;
    //window.location.href = `/campagne-de-smshing/details/${id}`;
  } else {
    //router.push(`/audits/phishing/details/${id}`);
    window.location.href = `/audits/phishing/details/${id}`;
    //window.location.href = `/campagne-de-phishing/details/${id}`;
  }
};

/*const tabs = ref([
  { route: '/audits', label: this.$t('audits.tabs.active'), icon: 'pi pi-chart-line' },
  { route: '/audits#audits-termines', label: this.$t('audits.completed'), icon: 'pi pi-check-circle' },
  { route: '/audits#rapports', label: this.$t('audits.tabs.reports'), icon: 'pi pi-file' }
]);*/

const tabs = ref([
  {
    route: "/audits",
    label: t("audits.tabs.active"),
    icon: "pi pi-chart-line",
  },
  {
    route: "/audits#audits-termines",
    label: t("audits.tabs.completed"),
    icon: "pi pi-check-circle",
  },
  {
    route: "/audits#rapports",
    label: t("audits.tabs.reports"),
    icon: "pi pi-file",
  },
]);

const subscription = ref(null);

const activeTab = ref(route.fullPath);

const searchQuery = ref("");
const selectedCampaigns = ref([]);
const campaigns = ref([]);
const campaignsSkeleton = ref(new Array(10));
const isLoading = ref(true);
const isTableLoading = ref(false);

// Ajouter cette configuration de filtres
const filters = ref({
  global: { value: null, matchMode: "contains" },
});

// Champs sur lesquels effectuer la recherche globale
const globalFilterFields = ["name", "status", "organisation.name"];

// Surveiller les changements dans searchQuery pour mettre à jour le filtre global
watch(searchQuery, (newValue) => {
  console.log(newValue);
  filters.value.global.value = newValue;
});

//
const handleNewCampaign = () => {
  if (subscription.value?.data && subscription.value?.remain_days > 0) {
    router.push("/audits/create");
  } else {
    console.log("error");
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: t("audits.please_subscribe"),
      life: 3000,
    });
  }
};

// Add date formatting function
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};
// Update status severity mapping created canceled Completed
const getStatusSeverity = (status) => {
  switch (status.toLowerCase()) {
    case "created":
      return "info";
    case "canceled":
      return "danger";
    case "closed":
      return "secondary";
    case "completed":
      return "success";
    case "queued":
      return "warn";
    case "in progress":
      return "warn";
    default:
      return "danger";
  }
};

const translateStatus = (status) => {
  const translations = {
    created: t("audits.status.created"),
    canceled: t("audits.status.canceled"),
    closed: t("audits.status.closed"),
    completed: t("audits.status.completed"),
    queued: t("audits.status.queued"),
    "in progress": t("audits.status.in_progress"),
  };
  return translations[status.toLowerCase()] || status;
};
const cancelDelete = () => {
  showDeleteModal.value = false;
  campaignToDelete.value = null;
  deleteConfirmationName.value = "";
};
const confirmDeleteCampaign = (campaign) => {
  campaignToDelete.value = campaign;
  deleteConfirmationName.value = "";
  showDeleteModal.value = true;
};
const goDeleteCampaign = async () => {
  if (
    !campaignToDelete.value ||
    deleteConfirmationName.value !== campaignToDelete.value.name
  ) {
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: t("audits.name_mismatch"),
      life: 3000,
    });
    return;
  }

  try {
    //loading.value = true

    const response = await deleteCampaign(
      campaignToDelete.value.id,
      campaignToDelete.value.type
    );

    if (response.data && response.status == 204) {
      toast.add({
        severity: "success",
        summary: t("common.success"),
        detail: t("audits.campaign_deleted"),
        life: 3000,
      });

      // Refresh data
      await fetchCampaigns();
      //await loadCampaignStats()
    } else {
      throw new Error("Delete failed");
    }
  } catch (error) {
    console.error("Error deleting campaign:", error);
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: t("audits.campaign_deleted_failed"),
      life: 3000,
    });
  } finally {
    //loading.value = false
    cancelDelete();
  }
};
onMounted(async () => {
  isLoading.value = true;
  campaigns.value = await fetchCampaigns();
  subscription.value = await fetchUserSubscription();
  isLoading.value = false;
  // Any initialization code can go here
  console.log(route, subscription.value);
});
watch(
  () => route.fullPath,
  (newPath) => {
    activeTab.value = newPath;
  }
);

const filteredCampaigns = computed(() => {
  let filtered = campaigns.value.campaigns;

  if (activeTab.value === "/audits") {
    filtered = filtered.filter((c) => c.status.toLowerCase() !== "completed");
  }
  if (
    activeTab.value === "/audits#audits-termines" ||
    activeTab.value === "/audits#rapports"
  ) {
    filtered = filtered.filter((c) => c.status.toLowerCase() === "completed");
  }

  return filtered;
});

const onPage = async (event) => {
  console.log(event);
  isTableLoading.value = true;
  campaigns.value = await fetchCampaigns(event.page, event.rows);
  isTableLoading.value = false;
};

// Add the downloadReport function:
const downloadReport = async (campaign) => {
  try {
    await downloadCampaignReport(campaign);
  } catch (error) {
    console.error("Failed to download report:", error);
    // You might want to show a toast notification here
  }
};
</script>

<style lang="scss" scoped>
@use "../../../assets/scss/variables.scss";
@use "../../../assets/scss/app/common.scss";

.text-little {
  @apply text-xs;
}
/* Example: set the background for the tab bar */
/*.mb-4.flex {
  background: linear-gradient(90deg, #0cbcc6 0%, #132c4e 100%);
  padding: 0.5rem 1rem;
  border-radius: 2rem;
}*/

/* You can further customize the active/inactive tab styles here if needed */

/* Status tag styling */
/*:deep(.p-tag.p-tag-success) {
  @apply bg-green-500 text-white;
}

:deep(.p-tag.p-tag-warning) {
  @apply bg-amber-500 text-white;
}

:deep(.p-tag.p-tag-info) {
  @apply bg-blue-500 text-white;
}
*/
</style>
